import styled from '@emotion/styled';
import { Select as $Select } from 'antd';
import { SelectProps as $SelectProps, DefaultOptionType } from 'antd/lib/select';
import { isArray, isString } from 'lodash';
import React from 'react';

import { OnBlurCallback, OnChangeCallback } from './index';

import theme from '../../theme';

// override `label` type to accept `string`
export type OptionProps = DefaultOptionType & { label: React.ReactNode | string | number };

export type SelectProps = Omit<$SelectProps, 'onBlur' | 'onChange'> & {
  children?: React.ReactNode[];
} & {
  name?: string;
  options?: OptionProps[];
  selectAllProperty?: boolean;
  onBlur?: OnBlurCallback;
  onChange?: OnChangeCallback;
  onSelect?: OnChangeCallback;
  // Note: Added to bypass errors related to option prop bypasses
  onDeselect?: (value: any, option: any) => void;
  filterOption?: boolean | ((inputValue: string, option?: any) => boolean);
  filterSort?: (optionA: any, optionB: any) => number;
};

export const SelectOption = $Select.Option;
export const SelectOptGroup = $Select.OptGroup;

// The "key=-1" comes from renderSelectAll method when to the option list is added Select All with key -1
function isSelectAll(value: string | string[], key: string) {
  return (isString(value) && key === '-1') || (isArray(value) && value.includes('all'));
}

export const Select = React.memo(function Select({
  name,
  options,
  children,
  selectAllProperty = false,
  onBlur,
  onChange,
  onSelect,
  size,
  ...rest
}: SelectProps) {
  const optionElements = options
    ? options.map(({ label, value, ...props }, index) => (
        <$Select.Option {...props} key={index} value={value} title={label as string}>
          {label}
        </$Select.Option>
      ))
    : children;

  const handleBlur =
    onBlur &&
    ((value: any) => {
      onBlur({ name, value });
    });

  const handleChange =
    onChange &&
    ((value: any, rest: { key: string; value: string; children: string; title: string }) => {
      if (isSelectAll(value, rest.key)) {
        onChange({
          name,
          value: options && value.length > options.length ? [] : options?.map((option) => option.value),
        });
      } else {
        onChange({ name, value });
      }
    });

  const handleSelect =
    onSelect &&
    ((value: any) => {
      onSelect({ name, value });
    });

  const renderSelectAll = () => {
    return rest.mode && rest.mode === 'multiple' ? (
      <$Select.Option key={-1} value="all" title="Select all">
        Select all
      </$Select.Option>
    ) : null;
  };

  return (
    <StyledSelect
      {...rest}
      size={size ?? 'large'}
      onBlur={handleBlur}
      onChange={handleChange}
      onSelect={handleSelect}
      getPopupContainer={(triggerNode) => triggerNode.parentElement as HTMLElement}
    >
      {selectAllProperty && renderSelectAll()}
      {optionElements}
    </StyledSelect>
  );
});

export const StyledSelect = styled($Select)`
  && {
    box-shadow: none !important;
    border-radius: 4px !important;
    line-height: 42px !important;
    border: 1px solid ${theme.colorsDisabledGreyButtons};
    outline: none !important;
    min-height: 44px;
    box-sizing: border-box;

    &.ant-select-disabled {
      background-color: #f5f5f5;
    }
  }

  &&:not(.ant-select-multiple) {
    height: 44px;
  }

  &&:hover,
  &&:focus-within {
    border-width: 2px !important;
    border-color: ${theme.colorsPrimaryBlue} !important;
  }

  &&::placeholder {
    color: #aaa;
  }

  && .ant-select-selector {
    height: 100% !important;
    border: none !important;
    padding: 2px 4px;
  }

  && .ant-select-selection-placeholder {
    line-height: 42px !important;
  }

  && input {
    height: 100% !important;
  }

  && .ant-select-selection-item {
    line-height: 42px !important;
    display: flex;
    align-items: center;
  }

  && .ant-select-selection-overflow-item {
    line-height: 32px;
  }

  && .ant-select-selection-overflow-item .ant-select-selection-item {
    padding: 0 10px;
    border: 1px solid #e8e8e8;
    border-radius: 2px;
    margin: 2px 4px 2px 0;
  }

  && .ant-select-selection-overflow-item .ant-select-selection-item-content {
    line-height: 2;
    color: rgba(0, 0, 0, 0.65);
    margin-right: 8px;
  }

  && .anticon-close {
    align-items: center;
    color: ${theme.colorsBlack};
    display: flex;

    svg {
      font-size: 14px;
    }
  }

  && .ant-select-selection-item-remove {
    display: flex;
  }
`;

export default Select;
